import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "rounded-lg bg-white p-4 shadow-sm" }
const _hoisted_2 = { class: "text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_3 = {
  key: 0,
  class: "text-[11px] text-gray-400"
}
const _hoisted_4 = { class: "rounded-lg bg-white p-4 shadow-sm" }
const _hoisted_5 = { class: "text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_6 = {
  key: 0,
  class: "mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-gray-100 px-3 py-2 tabular-nums"
}
const _hoisted_7 = { class: "text-center" }
const _hoisted_8 = { class: "text-sm font-bold leading-tight text-gray-800" }
const _hoisted_9 = { class: "text-[9px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_10 = { class: "text-center" }
const _hoisted_11 = { class: "text-sm font-bold leading-tight text-gray-800" }
const _hoisted_12 = { class: "text-[9px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_13 = { class: "text-base font-extrabold leading-tight text-gray-900" }
const _hoisted_14 = {
  key: 1,
  class: "mt-2 text-[11.5px] text-gray-400"
}
const _hoisted_15 = { class: "rounded-lg bg-white p-4 shadow-sm" }
const _hoisted_16 = { class: "mb-2 flex items-center gap-2" }
const _hoisted_17 = { class: "flex-1 truncate text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400" }
const _hoisted_18 = ["title"]
const _hoisted_19 = {
  key: 0,
  class: "flex items-center justify-center py-16"
}
const _hoisted_20 = {
  key: 2,
  class: "py-6 text-center text-xs italic text-gray-400"
}

import { computed } from 'vue';
import { parseISO } from 'date-fns';
import { AlertTriangle } from 'lucide-vue-next';
import merge from 'lodash-es/merge';
import GenericChart from '@/components/charts/GenericChart.vue';
import useDefaultChartOptions from '@/components/charts/composables/useDefaultChartOptions';
import Spinner from '@/components/ui/Spinner.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { useWaterNormSeries } from '@/plugins/store/actions/queries/active-groups';
import { directoryLocalDayStart } from './directoryLocalNow';
import type { SectionRow } from './sectionRow';
import { indexColourClass } from './indexColour';
import { computeFlagBands } from './flagBands';
import { formatActiveGroupsChartTooltipHeader, activeGroupsWeekAxisLabels } from './activeGroupsChartFormatting';
import DetailModalShell from './DetailModalShell.vue';
import PigTallyChart from './PigTallyChart.vue';

const placeholder = '—';


export default /*@__PURE__*/_defineComponent({
  __name: 'WaterDetailModal',
  props: {
    section: {},
    sections: {}
  },
  emits: ["close", "navigate", "handleAlarm"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

function onNavigate(target: SectionRow): void {
    emit('navigate', target);
}

function onHandleAlarm(target: SectionRow): void {
    emit('handleAlarm', target);
}

// On-demand water-norm fetch: enabled only while the modal is open (section != null).
const sectionId = computed(() => props.section?.id ?? null);
const { data: waterData, isLoading: isSeriesLoading } = useWaterNormSeries(sectionId);
const waterSeries = computed(() => waterData.value ?? []);

// The date the currently-displayed index was actually computed from (yesterday, or two days ago
// when yesterday's reading was unstable/missing — the backend's existing fallback). Empty when
// waterAsOfDate is unset (no usable day found, mirrors when the index itself is null).
const dayLabel = computed(() => {
    if (!props.section?.waterAsOfDate) return '';
    return i18n.global.d(parseISO(props.section.waterAsOfDate), 'middle') as string;
});

const hasUnstableDays = computed(() => waterSeries.value.some((point) => point.count_unstable));

// The unstable-day stretches as faint amber bands across the chart (same signal as the warning
// glyph next to the title).
const unstableBands = computed(() => computeFlagBands(waterSeries.value, (point) => Date.parse(point.date), (point) => point.count_unstable));

// App-wide chart conventions (localized axis, fonts, series palette) come from the shared wrapper;
// the custom-tooltip flag skips its per-point tooltip so the shared one below can show both series'
// values in a single box. No `type: ChartType.Stock` here — unlike the mortality modal's 12-months
// pig-tally series, the water-norm series never extends beyond the current Production Group (the
// backend always returns exactly Production Group-start → today), so there's nothing to navigate to.
const chartDefaults = useDefaultChartOptions({}, {}, true);

const chartOptions = computed(() => {
    const base: Record<string, unknown> = merge({} as Record<string, unknown>, chartDefaults.value);

    return merge(base, {
        chart: {
            height: 260,
            spacingLeft: 0,
            spacingRight: 0,
            backgroundColor: 'transparent',
            marginRight: 8,
        },
        exporting: { enabled: false },
        legend: { enabled: true, itemStyle: { color: '#6B7280', fontSize: '11px', fontWeight: '500' } },
        xAxis: {
            // Overrides the shared wrapper's global period-picker range: the series is already
            // scoped to the section's own Production Group, so it should always render in full.
            min: null,
            // The present moment, not the last datapoint - a Sensor that stopped reporting
            // days ago must render as empty space up to the right edge, not as a shortened axis.
            max: directoryLocalDayStart(props.section?.directoryLocalNow ?? null),
            labels: activeGroupsWeekAxisLabels,
            plotBands: unstableBands.value,
        },
        yAxis: {
            softMin: 0,
        },
        tooltip: {
            shared: true,
            split: false,
            formatter(this: any): string {
                const header = `<span style="font-size: 10px">${formatActiveGroupsChartTooltipHeader(this.x, this.points?.[0]?.series?.chart?.time)}</span>`;
                return (this.points ?? []).reduce(
                    (acc: string, point: any) =>
                        `${acc}<br/><span style="color:${point.color}">●</span> ${point.series.name}: <b>${i18n.global.n(point.y, 'decimal')}</b>`,
                    header
                );
            },
        },
        series: [
            {
                type: 'column',
                name: i18n.global.t('views.monitoring.activeGroups.modals.waterDetail.chartActualWater') as string,
                data: waterSeries.value.map((point) => [Date.parse(point.date), point.actual_water]),
                borderRadius: 1,
            },
            {
                type: 'line',
                name: i18n.global.t('views.monitoring.activeGroups.modals.waterDetail.chartNormWater') as string,
                data: waterSeries.value.map((point) => [Date.parse(point.date), point.norm_water]),
                marker: { enabled: false },
            },
        ],
    });
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DetailModalShell, {
    section: _ctx.section,
    sections: _ctx.sections,
    onClose: _cache[0] || (_cache[0] = ($event: any) => (emit('close'))),
    onNavigate: onNavigate,
    onHandleAlarm: onHandleAlarm
  }, {
    default: _withCtx(({ section: activeSection }) => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("h3", _hoisted_2, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.waterDetail.title')), 1),
        _createElementVNode("p", {
          class: _normalizeClass(["mt-2 text-[28px] font-extrabold leading-tight tracking-tight tabular-nums", activeSection.waterIndex !== null ? _unref(indexColourClass)(activeSection.waterIndex) : 'text-gray-300'])
        }, _toDisplayString(activeSection.waterIndex !== null ? activeSection.waterIndex : placeholder), 3),
        (dayLabel.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(dayLabel.value), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("h3", _hoisted_5, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.waterDetail.figuresTitle')), 1),
        (activeSection.waterActualWater !== null && activeSection.waterNormWater !== null)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$n(activeSection.waterActualWater, 'decimal')), 1),
                _createElementVNode("p", _hoisted_9, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.waterDetail.actualWater')), 1)
              ]),
              _cache[1] || (_cache[1] = _createElementVNode("span", { class: "text-sm font-semibold text-gray-300" }, "÷", -1)),
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("p", _hoisted_11, _toDisplayString(_ctx.$n(activeSection.waterNormWater, 'decimal')), 1),
                _createElementVNode("p", _hoisted_12, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.waterDetail.normWater')), 1)
              ]),
              _cache[2] || (_cache[2] = _createElementVNode("span", { class: "text-sm font-semibold text-gray-300" }, "× 100 =", -1)),
              _createElementVNode("p", _hoisted_13, _toDisplayString(activeSection.waterIndex !== null ? activeSection.waterIndex : placeholder), 1)
            ]))
          : (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(placeholder)))
      ]),
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("h3", _hoisted_17, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.waterDetail.chartTitle')), 1),
          (hasUnstableDays.value)
            ? (_openBlock(), _createElementBlock("span", {
                key: 0,
                class: "cursor-help",
                title: _ctx.$t('views.monitoring.activeGroups.modals.waterDetail.chartUnstableWarning')
              }, [
                _createVNode(_unref(AlertTriangle), {
                  class: "h-3.5 w-3.5 shrink-0 text-amber-500",
                  "aria-hidden": "true"
                })
              ], 8, _hoisted_18))
            : _createCommentVNode("", true)
        ]),
        (_unref(isSeriesLoading))
          ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
              _createVNode(Spinner, { class: "h-6 w-6" })
            ]))
          : (waterSeries.value.length)
            ? (_openBlock(), _createBlock(GenericChart, {
                key: 1,
                options: (chartOptions.value as any),
                expandable: false
              }, null, 8, ["options"]))
            : (_openBlock(), _createElementBlock("p", _hoisted_20, _toDisplayString(_ctx.$t('views.monitoring.activeGroups.modals.waterDetail.chartEmpty')), 1))
      ]),
      _createVNode(PigTallyChart, {
        "section-id": activeSection.id,
        day: activeSection.day,
        "directory-local-now": activeSection.directoryLocalNow
      }, null, 8, ["section-id", "day", "directory-local-now"])
    ]),
    _: 1
  }, 8, ["section", "sections"]))
}
}

})